import { useEffect, useRef, useState } from "react";
import {
    X,
    Check,
    Loader2,
    Paperclip,
    Trash2,
    Sparkles,
    History,
    Undo2,
    Image as ImageIcon,
    Copy,
} from "lucide-react";
import { api, fileUrl, thumbUrl } from "../lib/api";
import { toast } from "sonner";
import ReferencePicker from "./ReferencePicker";
import RetryImage from "./RetryImage";
import { useCurrencyPref } from "../pages/admin/_shared";

/**
 * Creative image editor — prompt-driven image-to-image.
 *
 * UX
 *   1. User picks an image-to-image model (same catalog as the chat:
 *      nano-banana, FLUX Kontext, gemini-flash-image, …).
 *   2. User optionally attaches reference images (passed natively to
 *      models that accept multiple image_urls).
 *   3. User describes the desired change in the prompt field.
 *   4. On apply we ping the backend without a composite — the backend
 *      sends the clean original to the image-generation service and preserves
 *      composition via the i2i prompt.
 *
 * Version history (created by every edit) is still available: all
 * previous revisions show up as a strip at the top and any of them can
 * be restored with one click without consuming credits.
 */
export default function ZoneEditor({
    chatId,
    messageId,
    img,
    onReplaced,
    onClose,
}) {
    const [prompt, setPrompt] = useState("");
    const [applying, setApplying] = useState(false);

    const [models, setModels] = useState([]);
    const [modelId, setModelId] = useState("");

    // Refs = auxiliary REFERENCIAS brought from the /references portfolio.
    // Apoyos = ad-hoc uploads from the user's disk via the paperclip.
    // Both are ultimately sent to the image-gen service as
    // `reference_image_urls` alongside the source image being edited, but
    // we keep them in separate state + separate UI strips so the user can
    // reason about them independently (the chat compose follows the exact
    // same convention).
    const [refs, setRefs] = useState([]);
    const [apoyos, setApoyos] = useState([]);
    const [uploadingApoyo, setUploadingApoyo] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const apoyoInputRef = useRef(null);

    /**
     * Version history — every edit keeps the original + all intermediate
     * versions in the library. On mount we fetch the chain so the user can
     * see how many times this image has been edited and jump back to any
     * prior version with a single click.
     */
    const [versions, setVersions] = useState([]);
    const [restoring, setRestoring] = useState(null);
    // Visibility toggles for the 3 auxiliary strips so the user can focus on
    // the preview without losing the ability to add more material.
    const [showHistory, setShowHistory] = useState(false);
    const [showRefs, setShowRefs] = useState(true);
    const [showApoyos, setShowApoyos] = useState(true);
    // Currency + TRM for displaying per-model price next to its name in the
    // dropdown. Kept in sync with the user's per-user toggle.
    const [backendCurrency, setBackendCurrency] = useState("USD");
    const [currency] = useCurrencyPref(backendCurrency);
    const [trm, setTrm] = useState(4000);

    useEffect(() => {
        api.get("/credits").then((r) => {
            setBackendCurrency(r.data.currency || "USD");
            setTrm(Number(r.data.trm_cop_per_usd || 4000));
        }).catch(() => {});
    }, []);

    /** Format the per-model COP value as USD or COP based on the current preference. */
    const fmtPriceInline = (cop) => {
        if (cop === undefined || cop === null) return "";
        if (currency === "USD") {
            const usd = trm > 0 ? cop / trm : 0;
            return ` · $${usd.toFixed(2)} USD`;
        }
        return ` · $${new Intl.NumberFormat("es-CO", {
            maximumFractionDigits: 0,
        }).format(cop)} COP`;
    };

    const loadHistory = async () => {
        try {
            const r = await api.get(`/library/${img.library_id}/history`);
            setVersions(r.data.versions || []);
        } catch {
            setVersions([]);
        }
    };

    useEffect(() => {
        loadHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [img.library_id]);

    const restore = async (targetId) => {
        if (restoring || targetId === img.library_id) return;
        setRestoring(targetId);
        try {
            const r = await api.post(
                `/chats/${chatId}/messages/${messageId}/images/${img.library_id}/restore/${targetId}`,
            );
            toast.success("Versión restaurada");
            onReplaced?.(r.data.image);
            onClose?.();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo restaurar la versión",
            );
        } finally {
            setRestoring(null);
        }
    };

    /* ---------- mount: lock scroll, fetch the SAME model catalog the chat uses ---------- */
    useEffect(() => {
        document.body.style.overflow = "hidden";
        api.get("/catalog/models")
            .then((r) => {
                // Show EXACTLY the same catalog as the chat — no more
                // editor-only filter. If a user picks a text-to-image
                // model we surface a warning below the select so they
                // know the base image won't be used as an input.
                const list = (r.data || []).filter(
                    (m) => m.kind !== "inpainting",
                );
                setModels(list);
                // Default: Nano Banana Pro (i2i edit) — best for branded
                // edits because it understands reference images. Fallback
                // chain: nano-banana-pro/edit → cheapest i2i → any i2i →
                // anything. This matches the Chat composer default.
                const i2iList = list.filter((m) => m.supports_image_input);
                const nanoBanana = i2iList.find(
                    (m) =>
                        m.id === "fal-ai/nano-banana-pro/edit" &&
                        m.enabled !== false,
                );
                const cheapest = [...i2iList]
                    .filter((m) => (m.cost_per_image_cop ?? null) !== null)
                    .sort(
                        (a, b) =>
                            (a.cost_per_image_cop || 0) -
                            (b.cost_per_image_cop || 0),
                    )[0];
                const picked = nanoBanana || cheapest || i2iList[0] || list[0];
                if (picked) setModelId(picked.id);
            })
            .catch(() => {});
        return () => {
            document.body.style.overflow = "";
        };
    }, []);

    /* ---------- references (from portfolio) ---------- */
    const addRefs = (picked) => {
        setRefs((prev) => {
            const seen = new Set(prev.map((r) => r.file_id));
            const next = [...prev];
            for (const p of picked) {
                if (!seen.has(p.file_id)) {
                    next.push(p);
                    seen.add(p.file_id);
                }
            }
            return next.slice(0, 4);
        });
    };
    const removeRef = (fileId) =>
        setRefs((prev) => prev.filter((r) => r.file_id !== fileId));

    /* ---------- apoyos (direct uploads) ---------- */
    const uploadApoyos = async (files) => {
        const list = Array.from(files || []).filter((f) =>
            f.type.startsWith("image/"),
        );
        if (list.length === 0) return;
        setUploadingApoyo(true);
        try {
            for (const f of list) {
                const fd = new FormData();
                fd.append("file", f);
                const r = await api.post("/files/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                const doc = r.data;
                setApoyos((prev) => [
                    ...prev,
                    {
                        file_id: doc.id,
                        storage_path: doc.storage_path,
                        title: doc.original_filename || "apoyo",
                    },
                ]);
            }
            toast.success(
                `${list.length} apoyo${list.length === 1 ? "" : "s"} subido${
                    list.length === 1 ? "" : "s"
                }`,
            );
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo subir el apoyo",
            );
        } finally {
            setUploadingApoyo(false);
        }
    };

    const onApoyoFileChange = (e) => {
        uploadApoyos(e.target.files);
        if (e.target) e.target.value = "";
    };

    const removeApoyo = (fileId) =>
        setApoyos((prev) => prev.filter((r) => r.file_id !== fileId));

    /* ---------- apply ---------- */
    const apply = async () => {
        if (applying) return;
        if (!prompt.trim()) {
            toast.error("Describe qué quieres cambiar en la imagen.");
            return;
        }
        if (!modelId) {
            toast.error("Elige un modelo image-to-image.");
            return;
        }
        setApplying(true);
        try {
            // Send references and apoyos separately so the backend can
            // apply the same cost formula as the chat composer:
            //   extras = max(0, n_refs - 1) + max(0, n_apoyos - 1)
            // Both lists also go inside `reference_image_ids` for
            // backwards compatibility with older backends that don't
            // know about `apoyo_image_ids` yet.
            const reference_image_ids = [
                ...refs.map((r) => r.file_id),
                ...apoyos.map((a) => a.file_id),
            ];
            const apoyo_image_ids = apoyos.map((a) => a.file_id);
            const r = await api.post(
                `/chats/${chatId}/messages/${messageId}/images/${img.library_id}/edit-zone`,
                {
                    prompt: prompt.trim(),
                    composite_base64: null,
                    model_id: modelId,
                    reference_image_ids,
                    apoyo_image_ids,
                },
            );
            toast.success("Edición aplicada");
            window.dispatchEvent(new CustomEvent("kc:credits-updated"));
            onReplaced?.(r.data.image);
            onClose?.();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo aplicar la edición.",
                { duration: 6000 },
            );
        } finally {
            setApplying(false);
        }
    };

    const currentModel = models.find((m) => m.id === modelId);

    return (
        <div
            data-testid="zone-editor-modal"
            className="fixed inset-0 z-[90] bg-ink-950/95 backdrop-blur-sm flex flex-col animate-fade-in"
        >
            {/* header */}
            <header className="h-16 shrink-0 flex items-center gap-4 px-6 border-b hairline border-ink-600">
                <Sparkles className="w-5 h-5 text-periwinkle-500" />
                <div className="min-w-0">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase">
                        Editor creativo · image-to-image
                    </p>
                    <h2 className="font-display text-xl text-fog-100 leading-tight">
                        Describe el cambio y deja que la IA edite
                    </h2>
                </div>
                <div className="flex-1" />
                <button
                    data-testid="zone-editor-close"
                    onClick={onClose}
                    className="w-9 h-9 rounded-full border hairline border-ink-600 text-fog-300 hover:text-fog-100 hover:border-periwinkle-500 flex items-center justify-center transition-colors"
                    aria-label="Cerrar"
                >
                    <X className="w-4 h-4" />
                </button>
            </header>

            {/* toolbar */}
            <div className="shrink-0 flex items-center gap-4 px-6 py-3 border-b hairline border-ink-600 flex-wrap">
                {/* model picker — same catalog as chat (only i2i-capable) */}
                <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] tracking-widest uppercase text-fog-400">
                        Modelo
                    </span>
                    <select
                        data-testid="zone-editor-model"
                        value={modelId}
                        onChange={(e) => setModelId(e.target.value)}
                        className="rounded-lg border hairline border-ink-600 bg-ink-900 text-fog-100 text-xs px-2 py-1.5 outline-none focus:border-periwinkle-500 min-w-[200px]"
                    >
                        {models.length === 0 && (
                            <option value="">Cargando…</option>
                        )}
                        {models.map((m) => (
                            <option key={m.id} value={m.id}>
                                {m.name}
                                {fmtPriceInline(m.cost_per_image_cop)}
                            </option>
                        ))}
                    </select>
                    {currentModel?.cost_per_image_cop ? (
                        <span
                            className="font-mono text-[10px] text-fog-500"
                            title={
                                currency === "USD"
                                    ? `≈ $${Number(
                                          currentModel.cost_per_image_cop,
                                      ).toLocaleString("es-CO", {
                                          maximumFractionDigits: 0,
                                      })} COP`
                                    : `≈ $${(
                                          (currentModel.cost_per_image_cop || 0) /
                                          (trm || 1)
                                      ).toFixed(2)} USD`
                            }
                        >
                            {currency === "USD"
                                ? `$${(
                                      (currentModel.cost_per_image_cop || 0) /
                                      (trm || 1)
                                  ).toFixed(2)} USD`
                                : `$${Number(
                                      currentModel.cost_per_image_cop,
                                  ).toLocaleString("es-CO", {
                                      maximumFractionDigits: 0,
                                  })} COP`}
                        </span>
                    ) : null}
                </div>

                <input
                    ref={apoyoInputRef}
                    data-testid="zone-editor-apoyo-upload-input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onApoyoFileChange}
                    className="hidden"
                />

                {/* Referencias: click → toggle visibility · "+" → open picker */}
                <StripToggle
                    label="Referencias"
                    icon={ImageIcon}
                    count={refs.length}
                    active={showRefs && refs.length > 0}
                    onToggle={() => {
                        if (refs.length > 0) setShowRefs((v) => !v);
                        else setPickerOpen(true);
                    }}
                    onAdd={() => setPickerOpen(true)}
                    tone="periwinkle"
                    testId="zone-editor-add-refs"
                />

                <StripToggle
                    label={uploadingApoyo ? "Subiendo…" : "Apoyos"}
                    icon={Paperclip}
                    count={apoyos.length}
                    active={showApoyos && apoyos.length > 0}
                    disabled={uploadingApoyo}
                    onToggle={() => {
                        if (apoyos.length > 0) setShowApoyos((v) => !v);
                        else apoyoInputRef.current?.click();
                    }}
                    onAdd={() => apoyoInputRef.current?.click()}
                    tone="amber"
                    testId="zone-editor-add-apoyos"
                />

                {versions.length > 1 && (
                    <StripToggle
                        label="Historial"
                        icon={History}
                        count={versions.length}
                        active={showHistory}
                        onToggle={() => setShowHistory((v) => !v)}
                        tone="periwinkle"
                        testId="zone-editor-toggle-history"
                    />
                )}

                <div className="flex-1" />
            </div>

            {/* References strip (from /references portfolio) */}
            {refs.length > 0 && showRefs && (
                <div
                    data-testid="zone-editor-refs-strip"
                    className="shrink-0 flex gap-2 px-6 py-2 border-b hairline border-ink-600 overflow-x-auto overflow-y-hidden items-center max-h-[84px]"
                >
                    <span className="font-mono text-[9px] tracking-widest uppercase text-periwinkle-500 shrink-0 pr-1">
                        Referencias · portafolio
                    </span>
                    {refs.map((r) => (
                        <div
                            key={r.file_id}
                            className="relative w-14 h-14 shrink-0 rounded-lg overflow-hidden border hairline border-periwinkle-500 group"
                            title={r.title || "Referencia"}
                        >
                            <RetryImage
                                src={thumbUrl(r.storage_path, 160)}
                                alt={r.title || ""}
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                            <span
                                className="absolute bottom-0 left-0 right-0 bg-periwinkle-500/90 text-white font-mono text-[7px] tracking-widest text-center uppercase py-0.5"
                                aria-hidden="true"
                            >
                                Referencia
                            </span>
                            <button
                                data-testid={`zone-editor-rm-ref-${r.file_id}`}
                                onClick={() => removeRef(r.file_id)}
                                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-white/90 text-ink-950 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                aria-label="Quitar referencia"
                            >
                                <Trash2 className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Apoyos strip (direct uploads) */}
            {apoyos.length > 0 && showApoyos && (
                <div
                    data-testid="zone-editor-apoyos-strip"
                    className="shrink-0 flex gap-2 px-6 py-2 border-b hairline border-ink-600 overflow-x-auto overflow-y-hidden items-center max-h-[84px]"
                >
                    <span className="font-mono text-[9px] tracking-widest uppercase text-amber-300 shrink-0 pr-1">
                        Apoyos · uploads
                    </span>
                    {apoyos.map((r) => (
                        <div
                            key={r.file_id}
                            className="relative w-14 h-14 shrink-0 rounded-lg overflow-hidden border hairline border-amber-400/60 group"
                            title={r.title || "Apoyo"}
                        >
                            <RetryImage
                                src={thumbUrl(r.storage_path, 160)}
                                alt={r.title || ""}
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                            <span
                                className="absolute bottom-0 left-0 right-0 bg-amber-400/85 text-ink-950 font-mono text-[7px] tracking-widest text-center uppercase py-0.5"
                                aria-hidden="true"
                            >
                                Apoyo
                            </span>
                            <button
                                data-testid={`zone-editor-rm-apoyo-${r.file_id}`}
                                onClick={() => removeApoyo(r.file_id)}
                                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-white/90 text-ink-950 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                aria-label="Quitar apoyo"
                            >
                                <Trash2 className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* version history strip */}
            {showHistory && versions.length > 1 && (
                <div
                    data-testid="zone-editor-history-strip"
                    className="shrink-0 border-b hairline border-ink-600 bg-ink-900 px-6 py-3"
                >
                    <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase mb-2 flex items-center gap-2">
                        <History className="w-3 h-3" />
                        Historial de ediciones · {versions.length} versiones
                    </p>
                    <div className="flex gap-3 overflow-x-auto pb-1">
                        {versions.map((v, i) => {
                            const isCurrent = v.id === img.library_id;
                            const promptText = v.message_prompt || "";
                            return (
                                <div
                                    key={v.id}
                                    data-testid={`zone-editor-version-${v.id}`}
                                    className={`group shrink-0 w-32 rounded-xl overflow-hidden border hairline transition-colors flex flex-col ${
                                        isCurrent
                                            ? "border-periwinkle-500 ring-2 ring-periwinkle-500/30"
                                            : "border-ink-600 hover:border-periwinkle-500"
                                    }`}
                                >
                                    <div className="relative aspect-square bg-ink-950 shrink-0">
                                        <RetryImage
                                            src={thumbUrl(v.storage_path, 160)}
                                            alt={`v${i + 1}`}
                                            loading="lazy"
                                            decoding="async"
                                            className="w-full h-full object-cover"
                                        />
                                        <span className="absolute top-1 left-1 rounded-full bg-periwinkle-500 text-white px-1.5 py-0.5 font-mono text-[9px] tracking-widest uppercase">
                                            v{i + 1}
                                        </span>
                                        {isCurrent && (
                                            <span className="absolute top-1 right-1 rounded-full bg-white/95 text-ink-950 px-1.5 py-0.5 font-mono text-[8px] tracking-widest uppercase">
                                                actual
                                            </span>
                                        )}
                                    </div>
                                    <div className="p-2 space-y-1.5 flex-1 flex flex-col min-h-0">
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="font-mono text-[9px] text-fog-400 truncate">
                                                {new Date(v.created_at).toLocaleString(
                                                    "es-CO",
                                                    {
                                                        day: "2-digit",
                                                        month: "2-digit",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    },
                                                )}
                                            </p>
                                            {promptText ? (
                                                <button
                                                    data-testid={`zone-editor-copy-prompt-${v.id}`}
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(
                                                            promptText,
                                                        );
                                                        toast.success("Prompt copiado");
                                                    }}
                                                    className="shrink-0 w-6 h-6 rounded-full border hairline border-ink-600 text-fog-300 hover:text-periwinkle-500 hover:border-periwinkle-500 flex items-center justify-center transition-colors"
                                                    title={`Copiar prompt: ${promptText.slice(0, 120)}${promptText.length > 120 ? "…" : ""}`}
                                                >
                                                    <Copy className="w-3 h-3" />
                                                </button>
                                            ) : (
                                                <span className="font-mono text-[9px] text-fog-500 italic shrink-0">
                                                    {i === 0 ? "Original" : "—"}
                                                </span>
                                            )}
                                        </div>
                                        {!isCurrent && (
                                            <button
                                                data-testid={`zone-editor-restore-${v.id}`}
                                                onClick={() => restore(v.id)}
                                                disabled={restoring === v.id}
                                                className="mt-auto w-full inline-flex items-center justify-center gap-1 rounded-md bg-periwinkle-500 hover:bg-periwinkle-400 text-white font-medium px-2 py-1 text-[11px] disabled:opacity-60"
                                            >
                                                {restoring === v.id ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : (
                                                    <Undo2 className="w-3 h-3" />
                                                )}
                                                Restaurar
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* preview — this image is the REFERENCIA (base point) */}
            <div
                data-testid="zone-editor-preview-area"
                className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 p-6 overflow-hidden"
            >
                <span
                    data-testid="zone-editor-base-badge"
                    className="inline-flex items-center gap-1.5 rounded-full bg-periwinkle-500/15 border hairline border-periwinkle-500/50 px-3 py-1 font-mono text-[9px] tracking-widest uppercase text-periwinkle-500 shrink-0"
                >
                    <Sparkles className="w-3 h-3" />
                    Referencia · punto base
                </span>
                <RetryImage
                    src={fileUrl(img.storage_path)}
                    alt="referencia base"
                    className="block flex-1 min-h-0 max-w-full rounded-xl border hairline border-periwinkle-500/40 shadow-glow-sm select-none object-contain"
                    draggable={false}
                />
            </div>

            {/* prompt + apply */}
            <footer className="shrink-0 border-t hairline border-ink-600 p-4 flex items-end gap-3">
                <div className="flex-1 rounded-xl border hairline border-ink-600 bg-ink-900 focus-within:border-periwinkle-500 transition-colors">
                    <textarea
                        data-testid="zone-editor-prompt"
                        rows={1}
                        value={prompt}
                        onChange={(e) => {
                            setPrompt(e.target.value);
                            // Auto-grow up to 180px; mirrors the chat composer
                            e.target.style.height = "auto";
                            e.target.style.height =
                                Math.min(e.target.scrollHeight, 180) + "px";
                        }}
                        placeholder="Describe qué quieres cambiar sobre la referencia (Shift+Enter para nueva línea)"
                        className="w-full bg-transparent px-4 py-3 text-sm text-fog-100 placeholder:text-fog-500 outline-none resize-none leading-relaxed min-h-[44px] max-h-[180px]"
                        onKeyDown={(e) => {
                            // Shift+Enter = newline (default). Enter alone = apply.
                            if (
                                e.key === "Enter" &&
                                !e.shiftKey &&
                                !e.nativeEvent.isComposing
                            ) {
                                e.preventDefault();
                                if (!applying && prompt.trim()) apply();
                            }
                        }}
                    />
                </div>
                <button
                    data-testid="zone-editor-apply"
                    onClick={apply}
                    disabled={applying}
                    className="inline-flex items-center gap-2 rounded-xl bg-periwinkle-500 hover:bg-periwinkle-400 text-white font-medium px-5 h-12 shadow-glow-sm transition-all disabled:opacity-60 disabled:cursor-wait"
                >
                    {applying ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Aplicando…
                        </>
                    ) : (
                        <>
                            <Check className="w-4 h-4" />
                            Aplicar
                        </>
                    )}
                </button>
            </footer>

            {pickerOpen && (
                <ReferencePicker
                    zClass="z-[100]"
                    onClose={() => setPickerOpen(false)}
                    onPick={(picked) => {
                        addRefs(picked);
                        setPickerOpen(false);
                    }}
                />
            )}

            {(refs.length > 0 || apoyos.length > 0) && (
                <div className="absolute top-32 right-6 max-w-xs rounded-lg border hairline border-periwinkle-500/40 bg-periwinkle-500/5 px-3 py-2 flex items-start gap-2 text-[11px] text-fog-300 pointer-events-none">
                    <Sparkles className="w-3.5 h-3.5 text-periwinkle-500 shrink-0 mt-0.5" />
                    <span>
                        Referencias y apoyos se envían al modelo junto con la
                        imagen base para guiar el estilo del resultado.
                    </span>
                </div>
            )}
        </div>
    );
}


/**
 * Compact toggle pill used in the editor toolbar for the three auxiliary
 * strips (Referencias, Apoyos, Historial). Two-part affordance:
 *   - The body toggles visibility of the strip.
 *   - The "+" chevron (when onAdd is provided and count > 0) opens the
 *     add-more modal / file picker without affecting visibility.
 *
 * `tone` drives the accent color family so each strip keeps its semantic
 * color (periwinkle for refs/history, amber for apoyos).
 */
function StripToggle({
    label,
    icon: Icon,
    count = 0,
    active,
    disabled,
    onToggle,
    onAdd,
    tone = "periwinkle",
    testId,
}) {
    const activeColors =
        tone === "amber"
            ? "border-amber-400 text-amber-200 bg-amber-500/10"
            : "border-periwinkle-500 text-periwinkle-500 bg-periwinkle-500/10";
    const inactiveColors =
        tone === "amber"
            ? "border-amber-400/40 text-amber-300/80 hover:border-amber-400 hover:text-amber-200"
            : "border-periwinkle-500/40 text-periwinkle-500/80 hover:border-periwinkle-500 hover:text-periwinkle-500";
    return (
        <div
            className={`inline-flex items-stretch rounded-lg border hairline overflow-hidden transition-colors ${
                active ? activeColors : inactiveColors
            } ${disabled ? "opacity-50" : ""}`}
        >
            <button
                data-testid={testId}
                onClick={onToggle}
                disabled={disabled}
                type="button"
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs"
                title={
                    active
                        ? `Ocultar ${label.toLowerCase()}`
                        : count > 0
                          ? `Mostrar ${label.toLowerCase()}`
                          : `Agregar ${label.toLowerCase()}`
                }
            >
                <Icon className="w-3.5 h-3.5" />
                {label}
                {count > 0 && (
                    <span className="font-mono text-[10px] opacity-80">
                        ({count})
                    </span>
                )}
            </button>
            {onAdd && count > 0 && (
                <button
                    data-testid={`${testId}-add`}
                    onClick={onAdd}
                    disabled={disabled}
                    type="button"
                    className="px-2 border-l hairline border-current/20 hover:bg-white/5 flex items-center"
                    title={`Agregar más ${label.toLowerCase()}`}
                >
                    <span className="text-base leading-none">+</span>
                </button>
            )}
        </div>
    );
}
